/**
 * Feature flags — primitives frontend
 *
 * - useSubscription() : récupère /api/subscription/me (cache 60s en module global)
 * - useModuleAccess(slug) : { enabled, reason, quotaUsed, quotaMax, degradedToMini }
 * - <ModuleGate slug="marketing" fallback={...}> : wrapper qui cache / désactive
 *
 * Le hook se rafraîchit aussi sur l'event "subscription-changed" pour les
 * mises à jour côté Super Admin.
 */

import { useEffect, useState, useCallback, createContext, useContext } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- Catalogue local des slugs (pour autocomplete IDE)
export const MODULES = {
  // Socle
  AUTH:              'auth',
  MULTI_TENANT:      'multi-tenant',
  DASHBOARD:         'dashboard',
  BRANDING:          'branding',
  PROPERTIES:        'properties',
  CONTACTS:          'contacts',
  TASKS:             'tasks',
  AI_CORE:           'ai-core',
  // Activables
  AVIS_VALEUR:       'avis-de-valeur',
  DOCUMENTS_IA:      'documents-ia',
  PRODUCTIVITE_EXT:  'productivite-externe',
  MARKETING:         'marketing',
  DIFFUSION:         'diffusion-portails',
  STATS_SIMU:        'stats-simulateurs',
  ASSISTANT_VOCAL:   'assistant-vocal',
  SITE_WEB_IA:       'site-web-ia',
  IMPORT_EXPORT:     'import-export',
};

// --- Cache mémoire global 60s
let _subscriptionCache = null;
let _subscriptionCacheTs = 0;
let _subscriptionPromise = null;
const TTL_MS = 60 * 1000;

async function fetchSubscription(force = false) {
  const now = Date.now();
  if (!force && _subscriptionCache && (now - _subscriptionCacheTs) < TTL_MS) {
    return _subscriptionCache;
  }
  if (_subscriptionPromise) return _subscriptionPromise;
  _subscriptionPromise = axios
    .get(`${API}/subscription/me`)
    .then((res) => {
      _subscriptionCache = res.data;
      _subscriptionCacheTs = Date.now();
      _subscriptionPromise = null;
      return res.data;
    })
    .catch((err) => {
      _subscriptionPromise = null;
      // Si 401 : on laisse l'intercepteur axios global gérer la redirection
      throw err;
    });
  return _subscriptionPromise;
}

export function invalidateSubscriptionCache() {
  _subscriptionCache = null;
  _subscriptionCacheTs = 0;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('subscription-changed'));
  }
}

// --- Hook bas niveau
export function useSubscription() {
  const [data, setData] = useState(_subscriptionCache);
  const [loading, setLoading] = useState(!_subscriptionCache);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const s = await fetchSubscription(true);
      setData(s);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchSubscription()
      .then((s) => { if (!cancelled) { setData(s); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e); setLoading(false); } });

    const onChange = () => refresh();
    window.addEventListener('subscription-changed', onChange);
    return () => {
      cancelled = true;
      window.removeEventListener('subscription-changed', onChange);
    };
  }, [refresh]);

  return { data, loading, error, refresh };
}

// --- Hook par module
export function useModuleAccess(slug) {
  const { data, loading } = useSubscription();
  if (loading || !data) {
    return { enabled: false, loading: true, reason: 'loading' };
  }
  const sub = data.subscription || {};
  const active = sub.active_modules || [];
  const enabled = active.includes(slug);
  const usage = data.usage || {};
  return {
    enabled,
    loading: false,
    reason: enabled ? null : 'module_disabled',
    quotas: sub.quotas || {},
    counts: data.counts || {},
    aiUsed: usage.ai_credits_used || 0,
    aiQuota: usage.ai_credits_quota || 0,
    aiDegraded: usage.ai_credits_used >= usage.ai_credits_quota,
  };
}

// --- Hook portail de diffusion
export function useDiffusionPortalAccess(portalKey /* 'cessionpme' | 'leboncoin' | 'seloger' */) {
  const { data, loading } = useSubscription();
  if (loading || !data) return { enabled: false, loading: true };
  const module = (data.subscription?.active_modules || []).includes(MODULES.DIFFUSION);
  if (!module) return { enabled: false, loading: false, reason: 'module_disabled' };
  const p = (data.subscription?.diffusion_portals || {})[portalKey];
  return {
    enabled: !!p?.enabled,
    loading: false,
    maxListings: p?.max_listings || 0,
    reason: p?.enabled ? null : 'portal_disabled',
  };
}
